import { useUpdateChatbotTrainingExampleMutation } from '../../features/chatbots/mutations/useChatbotTrainingExampleMutations';
import { useChatbotTrainingExampleQuery } from '../../features/chatbots/queries/useChatbotTrainingExampleQuery';
import { useI18n } from '../../i18n/useI18n';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import { CreateTrainingExampleDialog } from './CreateTrainingExampleDialog';
import { ChatbotConversationTrainingSourceStatusDialog } from './ChatbotConversationTrainingSourceStatusDialog';

type ChatbotConversationTrainingSourceDialogProps = {
  chatbotId: string;
  trainingExampleId: string;
  sourceTitle: string;
  isDeleted: boolean;
  canEdit: boolean;
  onClose: () => void;
};

export function ChatbotConversationTrainingSourceDialog({
  chatbotId,
  trainingExampleId,
  sourceTitle,
  isDeleted,
  canEdit,
  onClose,
}: ChatbotConversationTrainingSourceDialogProps) {
  const { translate } = useI18n();
  const exampleQuery = useChatbotTrainingExampleQuery(
    chatbotId,
    trainingExampleId,
    !isDeleted,
  );
  const updateMutation = useUpdateChatbotTrainingExampleMutation(chatbotId);

  const title = canEdit
    ? translate('chatbots.conversations.detail.editTrainingDialogTitle')
    : translate('chatbots.conversations.detail.viewTrainingDialogTitle');
  const closeLabel = translate('chatbots.training.close');
  const questionSnapshot = sourceTitle.trim();

  if (isDeleted) {
    return (
      <ChatbotConversationTrainingSourceStatusDialog
        title={title}
        message={translate(
          'chatbots.conversations.detail.trainingSourceDeleted',
        )}
        questionSnapshot={questionSnapshot}
        closeLabel={closeLabel}
        onClose={onClose}
      />
    );
  }

  if (exampleQuery.isLoading) {
    return (
      <ChatbotConversationTrainingSourceStatusDialog
        title={title}
        message={translate(
          'chatbots.conversations.detail.trainingSourceLoading',
        )}
        closeLabel={closeLabel}
        onClose={onClose}
      />
    );
  }

  if (exampleQuery.isError || exampleQuery.data == null) {
    return (
      <ChatbotConversationTrainingSourceStatusDialog
        title={title}
        message={translate(
          'chatbots.conversations.detail.trainingSourceLoadError',
        )}
        questionSnapshot={questionSnapshot}
        closeLabel={closeLabel}
        onClose={onClose}
      />
    );
  }

  const example = exampleQuery.data;

  return (
    <CreateTrainingExampleDialog
      key={example.id}
      title={title}
      questionLabel={translate('chatbots.training.questionLabel')}
      questionPlaceholder={translate('chatbots.training.questionPlaceholder')}
      answerLabel={translate('chatbots.training.answerLabel')}
      answerPlaceholder={translate('chatbots.training.answerPlaceholder')}
      sortOrderLabel={translate('chatbots.training.sortOrderLabel')}
      sortOrderPlaceholder={translate('chatbots.training.sortOrderPlaceholder')}
      sortOrderHint={translate('chatbots.training.sortOrderHint')}
      createLabel={translate('chatbots.training.save')}
      cancelLabel={translate('chatbots.training.cancel')}
      closeLabel={closeLabel}
      initialQuestion={example.question}
      initialAnswer={example.answer}
      initialSortOrder={example.sortOrder ?? null}
      isSubmitting={updateMutation.isPending}
      readOnly={!canEdit}
      showHowItWorks={false}
      onCreate={
        canEdit
          ? (input) => {
              updateMutation.mutate(
                {
                  example,
                  question: input.question,
                  answer: input.answer,
                  sortOrder: input.sortOrder,
                },
                {
                  onSuccess: () => {
                    toast.success({
                      title: translate('chatbots.training.updateSuccessTitle'),
                      description: '',
                    });
                    onClose();
                  },
                  onError: (error) => {
                    void toastMutationError(
                      error,
                      translate('chatbots.training.updateErrorTitle'),
                    );
                  },
                },
              );
            }
          : undefined
      }
      onClose={onClose}
    />
  );
}
